import { HttpClient } from '@angular/common/http';
import {Injectable, Pipe, PipeTransform, signal} from '@angular/core';
import { OrderService } from 'src/app/_services/order.service';
import { AppService } from 'src/app/app.service';
import { find, flatMap, get, each } from 'lodash';
import {ChatBasketService} from "./chat-basket.service";


@Pipe({
	name: 'cTranslate',
	pure: false,
	standalone: true
})

export class TranslateChatPipe implements PipeTransform {
	constructor(private chatService: ChatService) {

	}
	transform(value: any, args?: any): any {
		return this.chatService.translateKey(value, args);
	}
}

@Injectable({ providedIn: 'root' })
export class ChatService {
    private readonly apiUrl;
	STORAGE_KEY_SESSION_ID = 'chatbot-session-id';
	STORAGE_KEY_MESSAGES = 'chatbot-messages';


    private messagesSignal = signal<ChatMessage[]>(this.loadMessagesFromStorage());
    sessionId = signal(this.loadSessionIdFromStorage() ?? 'new');

    // Signal for system order requests - one order per session
    systemOrderRequest = signal<SystemOrderRequest | null>(null);

	loading = signal(false);
    cancelController: AbortController | null = null;
    messagePrefix = 'Answer only in ';
	$storage: any;

	questions= [
		{id: 'mealPlan', text: 'Meal recommendation', searchPattern:`MealRecommendationPattern`, fields: [
				{id: 'noAdults', text: 'Number of adults', type: 'toggle', min: 0, max: 5, step: 1, default: 2, required: true},
				{id: 'noChildren', text: 'Number of children', type: 'count', min: 0, max: 5, step: 1, default: 0, required: true},
				{id: 'budget', text: 'Budget', type: 'amount', min: 100, max: 600, step: 20, default: 300, required: true},
			]},
		{id: 'menuHelp', text: 'Find by properties', searchPattern: 'FindByPropsPattern', fields: [
				{id: 'category', text: 'Categories', type: 'chips', selectionType: 'single', onEmpty: 'כל הסוגים', members: [
						{value: 'Firsts'},
						{value: 'Mains'},
						{value: 'Salads'},
						{value: 'Drinks'},
						{value: 'Deserts'},
						{value: 'Soups'},
						{value: 'Sushi'},
					]},

				{id: 'characteristics', text: 'Properties', type: 'chips', selectionType: 'multi', required: true, members: [
						{value: 'Vegetarian'},
						{value: 'Spicy'},
						{value: 'Vegan'},
						{value: 'Gluten free'},
					]},
			]},
		{id: 'searchByPrice', text: 'Find by price', searchPattern: 'FindByPricePattern', fields: [
				{id: 'budget', text: 'Up to a price of', type: 'amount', min: 10, max: 300, step: 10, default: 50, required: true},
			]},
	];
	outOfStockParsed;

    constructor(
		private http: HttpClient,
		private appService: AppService,
		private orderService: OrderService,
		private chatBasketService: ChatBasketService
	) {

		this.$storage = this.orderService.$storage;
		this.orderService.getCatalogItemsAsFlatArray();

		const catalog = this.$storage.catalog;
		this.outOfStockParsed = this.orderService.catalogItemsAsFlatArray.filter(o => o.stockDisabled == true).map(o => o.offer);
		/*
		this.outOfStockParsed = Object.keys(catalog.inventory).map(key => {
			const item = catalog.items.find(i => i._id === key);
			return item.name;
		}).join(',');
		*/

		this.apiUrl = `${this.appService.appConfig.waiterChatbotAPI}/api/conversation/`;
	}

	translateKey(value, args?: any): string {
		const key = `_QK.${value}`;
		let translation, translationKeys = this.appService.aiLocale.keys;
		if (translationKeys) {
			translation = this.appService.translateKey(translationKeys, key, args);
		}
		if (!translation) {
			translation = this.appService.translateKey(this.appService.translations, key, args);
		}
		return translation || value;
	}

    // -------------------------------------------------------------------------------------------->
    // -------------------------------------------------------------------------------------------->
    // -------------------------------------------------------------------------------------------->

    async initChatbot(): Promise<void> {
		const isNewSession = this.getSessionId() === 'new';
		const basketMessage = this.chatBasketService.getBasketChatMessage();
		if (basketMessage) return this.sendMessage(basketMessage);
		if (isNewSession)  return this.sendMessage('');
    }

    async sendMessage(message?: string): Promise<any> {

        const isNewSession = this.getSessionId() === 'new';
        try {
            this.cancelController.abort('Cancelling Controller');
        } catch (error) {
            console.debug('AbortController already aborted:', error);
        }
        this.cancelController = new AbortController();
		this.loading.set(true);
        try {
            const organizationTime = isNewSession ? this.appService.getRealDate() : '';
            const stream = await this.sendMessageStream(
                this.appService.locale.localeId,
                this.appService.selectedLocale.name,
                message,
                this.cancelController.signal,
                organizationTime
            );
            const reader = stream.getReader();
            const decoder = new TextDecoder('utf-8');
            let fullText = '';


            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                fullText += chunk;
            }

            // Ensure final content is displayed for all streaming messages
            const processedMessage = this.processMessage({
                role: 'assistant',
                content: fullText,
                direction: this.appService.direction
            });
            this.messagesSignal.update(currentArray => [...currentArray, processedMessage]);
            this.saveMessagesToStorage(this.messagesSignal());
            return processedMessage;

        } catch (err: any) {
            if (err?.name !== 'AbortError') {
                console.error('Chat error:', err);
                this.addMessage({
                    role: 'assistant',
                    content: this.appService.translate('tabot.chat_error'),
                    type: 'error',
                    direction: this.appService.direction
                });
            }
        } finally {
            this.loading.set(false);
            this.cancelController = null;
        }
    }

    cancelOngoingRequest(): void {
        if (this.cancelController && !this.cancelController.signal.aborted) {
            try {
                // Use a more specific abort reason to help with debugging
                this.cancelController.abort('Component destroyed');
            } catch (error) {
                // Ignore abort errors as the request may already be completed or aborted
                console.debug('AbortController already aborted:', error);
            }
        }
        this.cancelController = null;
        this.loading.set(false);
    }

    /**
     * Formats message content by converting line breaks to <p> tags
     * @param message - The raw message content
     * @returns Formatted message with <p> tags
     */
    formatMessageWithParagraphs(message: string): string {
        if (!message) return message;

        // Split by line breaks and filter out empty lines
        const paragraphs = message.split(/\r?\n/).filter(line => line.trim().length > 0);

        // If only one paragraph, return as is
        if (paragraphs.length <= 1) {
            return message;
        }

        // Wrap each paragraph in <p> tags
        return paragraphs.map(paragraph => `<p>${paragraph.trim()}</p>`).join('');
    }

    // -------------------------------------------------------------------------------------------->
    // -------------------------------------------------------------------------------------------->
    // -------------------------------------------------------------------------------------------->

    // Expose conversation
    getConversation() {

        return this.messagesSignal.asReadonly();
    }

    addMessage(message: ChatMessage) {
        if (!message || typeof message !== 'object') {
            console.warn('Invalid message object:', message);
            return;
        }

        const processedMessage = this.processMessage(message);
        const updated = [...this.messagesSignal(), processedMessage];
        this.messagesSignal.set(updated);
        this.saveMessagesToStorage(updated);
    }
	clearLastMessage() {
		this.messagesSignal.update(currentItems =>
			currentItems.slice(0, -1)
		);
		this.saveMessagesToStorage(this.messagesSignal());
	}

    replaceConversation(messages: ChatMessage[]) {
        // Filter out invalid messages and process valid ones
        const validMessages = messages.filter(msg => msg && typeof msg === 'object' && msg.role && typeof msg.content === 'string');
        const processedMessages = validMessages.map(msg => this.processMessage(msg));
        this.messagesSignal.set(processedMessages);
        this.saveMessagesToStorage(processedMessages);
    }

    // Expose sessionId value
    getSessionId(): string {
        return this.sessionId();
    }

	cacheSessionAiChat(key, value, isSessionId = false) {
		this.orderService.$storage[key] = value;
		this.orderService.cacheOrder();
		/*if (isSessionId) {
			localStorage.setItem(key, value);
		} else {
			localStorage.setItem(key, JSON.stringify(value));
		}*/
	}

	clearSessionAiChat(key) {
		delete this.orderService.$storage[key];
		this.orderService.cacheOrder();
		//localStorage.removeItem(key);
	}

	getItemAiChat(key) {
		return this.orderService.$storage[key];
		//return localStorage.getItem(key);
	}

    updateSessionId(id: string) {
        this.sessionId.set(id);
        this.cacheSessionAiChat(this.STORAGE_KEY_SESSION_ID, id, true);
    }

    clearChatSession() {
        this.sessionId.set('new');
        this.messagesSignal.set([]);
        this.clearSessionAiChat(this.STORAGE_KEY_SESSION_ID);
        this.clearSessionAiChat(this.STORAGE_KEY_MESSAGES);
    }

    private formatTimeToIso(timeObj?: unknown): string {
        if (!timeObj) {
            return '';
        }

        // Handle objects with toISOString method (Date, moment, etc.)
        if (typeof timeObj === 'object' && timeObj !== null && 'toISOString' in timeObj) {
            const isoMethod = (timeObj as { toISOString: unknown }).toISOString;
            if (typeof isoMethod === 'function') {
                try {
                    return isoMethod.call(timeObj) as string;
                } catch {
                    return '';
                }
            }
        }

        // Fallback to string conversion
        return String(timeObj);
    }

    private getOrderTime(orgTime?: unknown): string {
		return this.formatTimeToIso(orgTime);
    }
/**
 * Builds the request body for chat API calls
 */
    private buildChatRequestBody(message: string, language: string, locale: string, orgTime?: any): string {
        const formattedMessage = `${message}`;

        const baseBody = {
            message: formattedMessage,
            sessionId: this.sessionId(),
			"timezone": "Asia/Jerusalem",
			"serviceType": this.$storage.service,
            locale: this.appService.aiLocale.id,
			skipWelcomeMessage: true,
        };

        // Add additional fields for new sessions
        if (this.sessionId() === 'new') {
            return JSON.stringify({
                ...baseBody,
                organization: this.orderService?.$storage?.siteId || '',
				outOfStock: this.outOfStockParsed,
				historyDetails: {},
                timestamp: this.getOrderTime(orgTime) || '',
            });
        }

        return JSON.stringify(baseBody);
    }
    // Streaming message support
    sendMessageStream(locale: string, language: string, message: string, signal?: AbortSignal, orgTime?: any): Promise<ReadableStream<Uint8Array>> {
        const body = this.buildChatRequestBody(message, language, locale, orgTime);
		return this.doFetch('stream-chat', body, signal).then((res) => {
            // Check if the response is ok before processing
            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }

            // Only update sessionId if the request was successful
            let sessionId = res.headers.get('X-Session-ID');
            if (sessionId) {
                sessionId = sessionId.replace(/"/g, '');
                this.updateSessionId(sessionId);
            }

            // Ensure response body exists
            if (!res.body) {
                throw new Error('No response body received from server');
            }

            return res.body;
        }).catch((error) => {
            // Log the error for debugging purposes
            console.error('Error in sendMessageStream:', error);

            // Re-throw the error to maintain the promise chain behavior
            // This ensures the calling code can handle the error appropriately
            throw error;
        });
    }

    async getChatOrder(locale: string): Promise<Response> {
        try {
			const body = JSON.stringify({sessionId: this.sessionId(), locale,});
            const response = await this.doFetch('update-user-changes', body);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return response;
        } catch (error) {
            console.error('Error fetching chat order:', error);
            throw error;
        }
    }

    // Alternative method that returns parsed JSON directly
    async getChatOrderData(locale: string): Promise<ChatOrderResponse> {
        const response = await this.getChatOrder(locale);
        return await response.json();
    }

    // Update user changes - streaming version
    updateUserChanges(locale: string, language: string, updateMessage: string, signal?: AbortSignal): Promise<ReadableStream<Uint8Array>> {
		const body = JSON.stringify({
			sessionId: this.sessionId(),
			updateMessage: this.messagePrefix + language + ': ' + updateMessage,
			locale
		})
        return this.doFetch('update-user-changes', body, signal).then((res) => {
            // Check if the response is ok before processing
            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }

            // Update sessionId if provided in response headers
            let sessionId = res.headers.get('X-Session-ID');
            if (sessionId) {
                sessionId = sessionId.replace(/"/g, '');
                this.updateSessionId(sessionId);
            }

            // Ensure response body exists
            if (!res.body) {
                throw new Error('No response body received from server');
            }

            return res.body;
        }).catch((error) => {
            // Only log errors that aren't intentional aborts
            if (!(error instanceof DOMException && error.name === 'AbortError')) {
                console.error('Error in updateUserChanges:', error);
            }

            // Re-throw the error to maintain the promise chain behavior
            // This ensures the calling code can handle the error appropriately
            throw error;
        });
    }

    // LocalStorage utilities
    private loadSessionIdFromStorage(): string | null {
        try {
            return this.getItemAiChat(this.STORAGE_KEY_SESSION_ID);
        } catch {
            return null;
        }
    }

    private loadMessagesFromStorage(): ChatMessage[] {
        try {
            const raw = this.getItemAiChat(this.STORAGE_KEY_MESSAGES);
            const messages = raw || [];

            // Validate messages are in correct format and process them
            if (!Array.isArray(messages)) {
                console.warn('Invalid messages format in storage:', messages);
                return [];
            }

            // Filter out invalid messages and process valid ones
            const validMessages = messages.filter(msg =>
                msg &&
                typeof msg === 'object' &&
                typeof msg.role === 'string' &&
                typeof msg.content === 'string'
            );

            return validMessages.map((message: ChatMessage) => this.processMessage(message));
        } catch (error) {
            console.warn('Error loading messages from storage:', error);
            return [];
        }
    }

    private saveMessagesToStorage(messages: ChatMessage[]) {
        try {
            // Filter out temporary assistant messages before saving
            const messagesToSave = messages.filter(message => message.role !== 'assistant-temporary');
            this.cacheSessionAiChat(this.STORAGE_KEY_MESSAGES, messagesToSave);
        } catch (err) {
            console.error('Failed to store messages:', err);
        }
    }

    /**
     * Removes messages containing command patterns from the conversation
     */
    removeCommandMessages(commandPattern: RegExp): void {
        const currentMessages = this.messagesSignal();
        const filteredMessages = currentMessages.filter(message =>
            !commandPattern.test(message.content)
        );

        if (filteredMessages.length !== currentMessages.length) {
            this.messagesSignal.set(filteredMessages);
            this.saveMessagesToStorage(filteredMessages);
        }
    }

    /**
     * Filters streaming content to hide @{...}@ patterns during streaming
     * This prevents showing partial JSON patterns to the user
     */
    filterStreamingContent(content: string): string {
        // Find the last occurrence of @{
        const lastAtIndex = content.lastIndexOf('@{');

        if (lastAtIndex === -1) {
            // No @{ found, return content as is
            return content;
        }

        // Check if there's a complete }@ after the last @{
        const textAfterAt = content.substring(lastAtIndex);
        const closingIndex = textAfterAt.indexOf('}@');

        if (closingIndex === -1) {
            // No closing }@ found, truncate from the last @{
            return content.substring(0, lastAtIndex);
        }

        // There is a complete pattern, return full content
        return content;
    }

	NN: number = 0
    /**
     * Processes message content by handling images, commands, and system order requests
     */
	offerViewCache: any[] = [] //used to cache response offer view
    private processMessage(message: ChatMessage): ChatMessage {
        if (message.processed) return message;
		let offers: any[] = [];

		const originalContent = message.content;
        let content = originalContent;

        // Ensure direction is set - default to 'ltr' if missing
        const direction = message.direction || 'ltr';

        // Check for commands and system requests on ORIGINAL content (before any filtering)
        this.handleCommands(originalContent);

        // Handle system order requests on ORIGINAL content
        const orderRequest = this.extractSystemOrderRequest(originalContent);
        let isSystemOrderMessage = false;
        if (orderRequest) {
            setTimeout(() => {
                this.handleSystemOrderRequest(orderRequest);
            }, 100);
            isSystemOrderMessage = true;
        }

		content = content.replace(/@{(.*?)}@/sg, (match, content) => {
			let response = '';
			try {
				const found = this.offerViewCache.find(o => o.content == content);
				if (found) return found.response;
				let json = match.slice(1, -1);
				const data = JSON.parse(json);
				const offerId = data.offerId;

				if (offerId && data.quantity) {
					const viewItem = find(this.orderService.catalogItemsAsFlatArray, { offer: offerId });
					if (viewItem) {
						offers.push({viewItem, data});
						if (get(data.modifications, 'mandatory')) {
							data.modifications = [...data.modifications.mandatory, get(data.modifications, 'optional', [])];
						}
						const dataIndex = this.cacheOfferDictionaryData(json);
						const imageURL = viewItem?.imageURL || '/assets/images/restaurant-logo-not-found.png';

						let modificationsParsed = '';
						if (viewItem._disabled) {
							modificationsParsed = `<b class="text-danger">${this.appService.translate('tabot.out_of_stock')}</b>`;
						}

						response = `<div
							class="offer-row ${viewItem._disabled ? 'disabled' : ''} ${!viewItem._disabled && viewItem._enableEdit ? 'editable' : ''}"
							data-offerid="${offerId}"
							data-viewitem-id="${viewItem._id}"
							data-index="${dataIndex}">
							<img src="${imageURL}" loading="lazy" alt="Chat image" />
							<div>
								<div class="_title">${data.quantity > 1 ? (data.quantity + '* ') : ''}${this.getFallBack(data, ['itemName', 'Item_Name'], viewItem.name) || ''}</div>
								${modificationsParsed}
								<div class="_description text-info">${this.getFallBack(data, ['itemDescriptionMDFY','Item_Description_MDFY'], viewItem.description) || ''}</div>
							</div>
						</div>`.replace(/\n/g, '');


					} else {
						response = `<div class="text-danger">---Not in the client's view---</div>`
					}
				}
			} catch (e) {
				console.error('Error parsing offer patter:', content);
			}
			this.offerViewCache.push({content: content, response: response.replace(/zoomIn/g, '')});
			return response; // Keep original pattern if no image found
		});

		// remove all remarks
		content = content.replace(/<!--[\s\S]*?-->/g, '');

        // Remove ALL complete @{...}@ patterns from display content
        const allPatternsRegex = /@\{[^}]*\}@/g;
        content = content.replace(allPatternsRegex, '');

        // Handle bold markdown patterns
        const boldPattern = /\*\*(.*?)\*\*/g;
        content = content.replace(boldPattern, '<strong>$1</strong>');
        const doubleLineBreakPattern = /\n\n/g;
        content = content.replace(doubleLineBreakPattern, '<br/>');
        const singleLineBreakPattern = /\n/g;
        content = content.replace(singleLineBreakPattern, '<br/>');

        return {
            ...message,
            content,
            direction,
            hidden: isSystemOrderMessage,
			offers,
			processed: !message.provisional
        };
    }

	getFallBack(obj, options:string[], _default?: any) {
		for (const prop of options) {
			if (obj[prop]) return obj[prop];
		}
		return _default || null
	}

	cacheOfferDictionaryData(data){
		let chatDictionary = this.orderService.$storage.chatDictionary;
		if (!chatDictionary) {
			this.orderService.$storage.chatDictionary = [data];
			return 0;
		}
		let index = chatDictionary.indexOf(data);
		if (index >= 0) return index;
		chatDictionary.push(data);
		return chatDictionary.length -1;
	}
	getOfferDictionaryData(index: number) {
		let chatDictionary = this.orderService.$storage.chatDictionary;
		if (chatDictionary && !isNaN(index)) {
			const json = chatDictionary[index];
			if (json) {
				try {
					const data = JSON.parse(json);
					return data;
				} catch (e) {

				}
			}
		}
	}

    /**
     * Extracts and parses system order request from message content
     */
    private extractSystemOrderRequest(content: string): SystemOrderRequest | null {
        console.log('extractSystemOrderRequest called with content:', content);
        // More flexible regex to handle various <br/> patterns and missing closing brace
        const orderRequestPattern = /@\{"System_request_order":\[([\s\S]*?)\](?:<br\s*\/?>)*\}?@/;
        const match = content.match(orderRequestPattern);

        if (!match) {
            console.log('No system order request pattern found');
            return null;
        }

        console.log('Found system order request pattern:', match[0]);

        try {
            // Clean the matched content from HTML tags like <br/>
            const cleanedMatch = match[1].replace(/<br\s*\/?>/gi, '').replace(/\s+/g, ' ').trim();
            const orderRequestString = `{"System_request_order":[${cleanedMatch}]}`;
            console.log('Parsing order request string:', orderRequestString);
            const parsedRequest = JSON.parse(orderRequestString) as SystemOrderRequest;
            console.log('Successfully parsed system order request:', parsedRequest);
            return parsedRequest;
        } catch (error) {
            console.error('Failed to parse system order request:', error);
            return null;
        }
    }

    private handleSystemOrderRequest(orderRequest: SystemOrderRequest): void {
        console.log('handleSystemOrderRequest called with:', orderRequest);
        // Set the system order request signal - one order per session
        this.systemOrderRequest.set(orderRequest);
    }

    /**
     * Handles command execution - to be implemented by components
     */
    private commandHandlers: Map<string, () => void> = new Map();

    private handleCommands(content: string): void {
        const commandPattern = /@\{"command":"([^"]+)"}@/g;
        let match;

        while ((match = commandPattern.exec(content)) !== null) {
            const command = match[1];
            const handler = this.commandHandlers.get(command);
            if (handler) {
                setTimeout(() => handler(), 0);
            }
        }
    }

  	//--------------------------------------------------------------------------------------------->
	//--------------------------------------------------------------------------------------------->
	//--------------------------------------------------------------------------------------------->
	//--------------------------------------------------------------------------------------------->

	doFetch(url, body, signal?) {
		const token = localStorage.getItem('token') || sessionStorage.getItem('token');
		return fetch(this.apiUrl + url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			body,
			signal,
		})
	}


}




export type ChatMessageType = 'normal' | 'processing' | 'error' | 'feedback' | 'empty' | 'voice';

export interface ChatMessage {
	role: 'user' | 'assistant' | 'assistant-first' | 'assistant-temporary';
	content: string;
	type?: ChatMessageType;
	direction?: 'ltr' | 'rtl';
	hidden?: boolean;
	offers?: any[],
	processed?: boolean,
	provisional?: boolean,
}

export interface OfferModifier {
	modifierGroupId: string;
	modifierId: string;
	modifier: string;
	action?: string;
}
export interface ImageDetails {
	name: string;
	description: string;
	imageURL: string;
}

export interface OfferSelection {
	item: string;
	name: string;
	price?: number;
	itemGroup: string;
	itemGroupName?: string;
	selectedModifiers?: OfferModifier[];
}

export interface Offer {
	readonly offer: string;
	readonly name: string;
	readonly price?: number;
	readonly selectedModifiers?: OfferModifier[];
	readonly removedModifiers?: OfferModifier[];
	readonly selections?: OfferSelection[];
}

export type ChatOrderStatusCode = 'processing' | 'completed' | 'error' | 'empty' | 'feedback';

export interface ChatOrderResponse {
	textResponse: string;
	isOfferValid: boolean;
	offers?: Offer[];
	statusCode?: ChatOrderStatusCode;
}

export interface UpdateUserChangesRequest {
	sessionId: string;
	menu: string;
	updateMessage: string;
}

export interface OrderItem {
	offerId: string;
	quantity: number;
	modifications: Array<{
		action?: any;
		modificationId: string;
		choice: string;
	}>;
	selections: Array<{
		action?: any;
		selectionId: string;
		choice: string;
		modifications: Array<{
			action?: any;
			modificationId: string;
			choice: string;
		}>;
	}>;
}

export interface SystemOrderRequest {
	System_request_order: OrderItem[];
}
